import cn from 'classnames';
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { WidgetTechnology } from '@services/df/technologies';
import { ObjectModel } from '@types';
import { selectOrderLoaded } from '@modules/order';
import {
    selectWidgetModelId,
    selectWidgetMaterialId,
    selectWidgetMaterial,
    selectWidgetPriceConfig,
    selectWidgetCount,
} from '@modules/quotation';
import { useCalculatePrice, useFindProduct, usePostProductionPrice } from '@pages/widget/order/hooks';

import styles from './calculation-section.module.scss';

interface Props {
    model?: ObjectModel;
    technology?: WidgetTechnology;
}

export const CalculationSection: React.FC<Props> = ({ model, technology, children }) => {
    const dispatch = useAppDispatch();

    const orderExists = useAppSelector(selectOrderLoaded);
    const parentModelId = useAppSelector(selectWidgetModelId);
    const materialId = useAppSelector(selectWidgetMaterialId);
    const hasPostProduction = useAppSelector(state => Boolean(selectWidgetMaterial(state)?.post_production.length));
    const config = useAppSelector(selectWidgetPriceConfig);
    const count = useAppSelector(selectWidgetCount);

    const calculate = useCalculatePrice();
    useEffect(() => {
        calculate(count, !!model && !model.parent_model, parentModelId, technology?.id, materialId, config);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, parentModelId, materialId]);

    const findProduct = useFindProduct();
    useEffect(() => {
        if (orderExists) {
            findProduct(parentModelId, materialId, config);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderExists, parentModelId, materialId, config]);

    const getPostProductionPricing = usePostProductionPrice();
    useEffect(() => {
        getPostProductionPricing({ materialId, parentModelId, hasPostProduction, config });
    }, [dispatch, getPostProductionPricing, hasPostProduction, materialId, parentModelId, config]);

    return (
        <div className={cn('rounded-box', 'overflow-clip', 'flex', 'flex-col', 'justify-between', styles.box)}>
            {children}
        </div>
    );
};
